import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Copy, Check, Loader2, Store, MapPin, ShieldCheck, CreditCard, Building2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { fmtNaira } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public payment page — anyone can pay any Kobolog trader by URL.
 *
 * Route: /@:tag    e.g. /@mama-bola
 *
 * No login required. Two payment paths shown:
 *   1) Direct bank transfer (DVA) — copy-paste account #
 *   2) Card via Paystack inline checkout
 *
 * Designed to be shared via WhatsApp, SMS, or printed on receipts/posters.
 */
export default function PublicPay() {
  const { tag } = useParams();
  const [trader, setTrader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    let aborted = false;
    axios.get(`${API}/cashtag/${encodeURIComponent(tag)}`, { signal: ctl.signal })
      .then((r) => { if (!aborted) setTrader(r.data); })
      .catch((e) => {
        if (axios.isCancel?.(e) || e.code === "ERR_CANCELED") { aborted = true; return; }
        setError(e.response?.status === 404 ? "This Kobolog tag doesn't exist." : "Couldn't load page. Try again.");
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; ctl.abort(); };
  }, [tag]);

  const copyAccount = async () => {
    if (!trader?.dva) return;
    try {
      await navigator.clipboard.writeText(trader.dva.account_number);
      setCopied(true);
      toast.success("Account number copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const payByCard = async () => {
    const amt = Number(amount);
    if (!amt || amt < 100) { toast.error("Enter an amount of at least ₦100"); return; }
    setPaying(true);
    try {
      const r = await axios.post(`${API}/cashtag/pay`, {
        cashtag: trader.cashtag,
        amount: amt,
        customer_name: name || undefined,
        customer_email: email || undefined,
        description: note || undefined,
      });
      // Meta Pixel: fire InitiateCheckout before redirect. Value is Naira.
      try {
        if (typeof window.fbq === "function") {
          window.fbq("track", "InitiateCheckout", {
            value: amt,
            currency: "NGN",
            content_name: trader.business_name || trader.name || "Kobolog payment",
            content_category: "cashtag",
          });
        }
      } catch { /* noop */ }
      // Redirect to Paystack hosted checkout — works on any device, no SDK needed.
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed. Try again.");
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="min-h-dvh flex items-center justify-center bg-kudi-cream">
      <Loader2 className="w-7 h-7 text-kudi-green animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-dvh flex items-center justify-center bg-kudi-cream px-6">
      <div className="max-w-sm w-full bg-white border border-border rounded-3xl p-8 text-center">
        <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-lg font-bold mb-1">Tag not found</h1>
        <p className="text-sm text-kudi-ink/60 mb-5" data-testid="pay-error">{error}</p>
        <a href="/" className="inline-block bg-kudi-green text-white rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="kobolog-link">Back to home</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-gradient-to-br from-kudi-cream via-white to-accent">
      <div className="max-w-md mx-auto px-5 py-6 sm:py-10">

        {/* Trader header */}
        <div className="bg-white border border-border rounded-3xl p-5 shadow-sm" data-testid="trader-header">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-kudi-green to-emerald-700 text-white flex items-center justify-center font-heading text-2xl font-bold shrink-0">
              {(trader.owner_name || trader.business_name || "K").charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-xl font-bold truncate">{trader.business_name || trader.owner_name}</h1>
              {trader.business_name && trader.owner_name && (
                <div className="text-sm text-kudi-ink/65 truncate">{trader.owner_name}</div>
              )}
              <div className="text-xs text-kudi-green font-semibold mt-0.5 flex items-center gap-1">
                <span data-testid="cashtag-label">@{trader.cashtag}</span>
                <ShieldCheck className="w-3.5 h-3.5" />
                <span className="text-kudi-ink/55 font-normal">Verified by Kobolog</span>
              </div>
            </div>
          </div>
          {(trader.business_type || trader.location) && (
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-kudi-ink/60">
              {trader.business_type && <span className="flex items-center gap-1"><Store className="w-3.5 h-3.5" /> {trader.business_type}</span>}
              {trader.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {trader.location}</span>}
            </div>
          )}
        </div>

        {/* Payment options */}
        <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-kudi-ink/55 mt-6 mb-2 px-1">Pay {trader.owner_name?.split(" ")[0] || "this trader"}</h2>

        {/* Card via Paystack */}
        <div className="bg-white border border-border rounded-3xl p-5 mt-3" data-testid="pay-card-block">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <CreditCard className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading font-semibold text-sm">Pay with card or bank</div>
              <div className="text-xs text-kudi-ink/55">Powered by Paystack. Cards, USSD, bank.</div>
            </div>
          </div>
          <div className="space-y-2.5">
            <Field label="Amount (₦)" value={amount} onChange={setAmount} type="number" placeholder="5000" testid="pay-amount" />
            <Field label="Your name (optional)" value={name} onChange={setName} placeholder="e.g. Tunde" testid="pay-name" />
            <Field label="Email (optional)" value={email} onChange={setEmail} type="email" placeholder="your@email.com" testid="pay-email" />
            <Field label="Note (optional)" value={note} onChange={setNote} placeholder="e.g. Half payment, balance Friday" testid="pay-note" />
          </div>
          <button
            onClick={payByCard}
            disabled={paying || !amount}
            className="mt-3 w-full bg-kudi-green text-white rounded-full py-3.5 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-green-dark transition disabled:opacity-50 active:scale-[0.98]"
            data-testid="pay-card-btn"
          >
            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay {amount ? fmtNaira(Number(amount)) : "now"} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>

        {/* Direct bank transfer */}
        {trader.dva ? (
          <div className="bg-white border border-border rounded-3xl p-5 mt-3" data-testid="pay-bank-block">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading font-semibold text-sm">Or send by bank transfer</div>
                <div className="text-xs text-kudi-ink/55">Goes directly to {trader.owner_name?.split(" ")[0] || "the trader"}&apos;s wallet.</div>
              </div>
            </div>
            <button onClick={copyAccount} className="w-full bg-accent/40 border border-border rounded-2xl p-3.5 flex items-center gap-3 hover:bg-accent transition group" data-testid="copy-account-btn">
              <div className="flex-1 text-left">
                <div className="text-[11px] text-kudi-ink/55 uppercase tracking-wider font-semibold">{trader.dva.bank_name}</div>
                <div className="font-heading text-2xl font-bold tabular-nums tracking-wide" data-testid="dva-account-number">{trader.dva.account_number}</div>
                <div className="text-xs text-kudi-ink/65 mt-0.5 truncate">{trader.dva.account_name}</div>
              </div>
              <div className="shrink-0 w-9 h-9 rounded-full bg-white border border-border flex items-center justify-center group-hover:border-kudi-green transition">
                {copied ? <Check className="w-4 h-4 text-kudi-green" /> : <Copy className="w-4 h-4 text-kudi-ink/55" />}
              </div>
            </button>
            <p className="text-[11px] text-kudi-ink/50 mt-2.5">Transfers auto-log in their Kobolog wallet within seconds via Paystack.</p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-4 mt-3 text-xs text-amber-900" data-testid="no-dva-block">
            Bank transfer not enabled for this trader yet — use the card option above.
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6 mb-2">
          <a href="/" className="text-xs text-kudi-ink/45 hover:text-kudi-green transition" data-testid="kobolog-link">
            <span className="font-heading font-bold text-kudi-green">kobolog</span> · every kobo, logged.
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] text-kudi-ink/55 uppercase tracking-wider font-semibold">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-accent/40 border border-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-kudi-green/30 focus:border-kudi-green/40 transition"
        data-testid={testid}
      />
    </label>
  );
}
